import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWindowWidth } from '../hooks/useWindowWidth.js';
import { streamHelpbotAsk, sendHelpbotFeedback } from '../services/helpbotService.js';
import Modal from './Modal.jsx';

const MODULE_LABELS = {
  production:              'Production',
  quality:                 'Quality',
  warehouse:               'Warehouse',
  sales:                   'Sales',
  finance:                 'Finance',
  profiles:                'Profiles',
  training:                'Training',
  'document-control':      'Document Control',
  'ingredient-management': 'Ingredient Management',
};

// Guide section heading (as it appears under "## Section reference" in
// littoral-user-guide.md) -> primary app route for that section. First link
// in that section's NAV_SECTIONS group in Layout.jsx, one small add-on, not
// meant to cover every deep link -- clicking the citation just gets the user
// to the right neighborhood.
const SECTION_ROUTES = {
  'PROFILES':             '/profiles/company-master',
  'QUALITY':              '/coa-records',
  'PRODUCTION':           '/production/batch?tab=records',
  'WAREHOUSE':            '/warehouse/receiving?tab=receipts',
  'TRAINING':             '/quality/training-programs',
  'SALES':                '/profiles/companies',
  'FINANCE':              '/ap-invoices',
  'SAFETY':               '/safety/pest?tab=inspections',
  'DOCUMENT CONTROL':     '/admin/sop-library',
  'REPORTS':              '/reports/revenue-by-customer',
  'INGREDIENT MANAGEMENT': '/ing/catalog',
};

// HELPBOT-CITATION-ROUTE-PRECISION-1: SECTION_ROUTES alone sends every citation
// to one static route per section (e.g. all of QUALITY -> /coa-records), even
// though the guide names a specific page per bullet. This maps the guide's
// bold "## Section reference" page names to their real route (source: the
// same `to` values Layout.jsx's NAV_SECTIONS/TAB_SEARCH_ENTRIES use) so a
// citation like "→ See: PRODUCTION → Batch Costing Approvals" lands on
// /approvals/batch-costing instead of the generic Production landing page.
// Not exhaustive — covers the guide's bold page names; anything else falls
// back to SECTION_ROUTES.
const PAGE_ROUTES = {
  'COMPANY MASTER':               '/profiles/company-master',
  'FORM 1087':                    '/form-1087',
  'VENDOR APPROVAL PROGRAM (VAP)': '/vap-records',
  'DOCUMENT INTAKE TRAY':         '/vap-intake-pending',
  'MATERIAL PROFILES':            '/profiles/materials',
  'EQUIPMENT':                    '/equipment',
  'APPROVED PRODUCTS':            '/customer-approved-products',

  // NAV-SECTION-FOLD-1 (2026-09-19): Quality routes updated for the fold —
  // several bare page names now resolve into a specific hub tab.
  'CERTIFICATE OF ANALYSIS':      '/coa-records',
  'QUARANTINE RECORDS':           '/quality/deviations?tab=quarantine',
  'TEMPORARY DEVIATIONS':         '/quality/deviations?tab=temp-deviations',
  'ENVIRONMENTAL MONITORING':     '/quality/environmental-monitoring',
  'NON-CONFORMANCE':              '/quality/non-conformance',
  'CORRECTIVE ACTIONS':           '/quality/corrective-actions',
  'CALIBRATION':                  '/quality/calibration',
  'MOC':                          '/quality/deviations?tab=moc',
  'CUSTOMER COMPLAINTS':          '/quality/non-conformance?tab=complaints',
  'INTERNAL AUDITS':              '/quality/audits-reviews?tab=internal-audits',
  'ANNUAL PRODUCT REVIEWS':       '/quality/audits-reviews?tab=annual-product-reviews',
  'MANAGEMENT REVIEWS':           '/quality/audits-reviews?tab=management-reviews',
  'QUALITY RESPONSIBILITY':       '/quality/audits-reviews?tab=quality-responsibility',
  'CONTINUOUS IMPROVEMENT LOG':   '/quality/improvement-comms?tab=ideas',
  'EXTERNAL COMMUNICATIONS':      '/quality/improvement-comms?tab=external-comms',
  'FOREIGN MATERIAL TESTING':     '/quality/calibration?tab=sediment',
  'ALLERGEN DECLARATIONS':        '/quality/food-safety?tab=allergen-declarations',
  'SAMPLE SUBMISSIONS':           '/warehouse/samples?tab=external-submissions',
  'QUALITY CONFIG':               '/quality/config',
  'SUSTAINABILITY':               '/quality/sustainability',
  'LABEL ISSUANCE':               '/production/labels?tab=issuance',
  'ASSET LIBRARY':                '/production/labels?tab=assets',

  // NAV-SECTION-FOLD-1 §2 (2026-09-19): Cost Pool Settings, Blend Room Ops and
  // Label Printers folded into tabs on other Production hubs.
  'BATCH RECORDS':                '/production/batch?tab=records',
  'BLEND ROOM OPS':               '/production/batch?tab=blend-room-ops',
  'BATCH COSTING APPROVALS':      '/approvals/batch-costing',
  'COST POOL SETTINGS':           '/approvals/batch-costing?tab=cost-pool',
  'PRODUCTION SCHEDULE':          '/production/schedule?tab=schedule',
  // MRP-EXECUTION-SPLIT-1 (2026-09-20): former single "MRP Planning" bold page
  // name split into two hub pages in the guide.
  'DEMAND & PLANNING':            '/planning/mrp',
  'MRP EXECUTION':                '/planning/mrp-execution',
  'EQUIPMENT LOGS':               '/production/equipment?tab=logs',
  'ACCOUNTABILITY LOGS':          '/production/accountability?tab=scoops',
  'CLEANING':                     '/production/cleaning?tab=records',
  'MAINTENANCE':                  '/production/maintenance?tab=requests',
  'LABEL PRINTERS':               '/production/labels?tab=printers',

  'RECEIVING':                    '/warehouse/receiving?tab=receipts',
  'DOCUMENTS':                    '/documents',
  'INVENTORY':                    '/warehouse/inventory?tab=lots',
  'SAMPLES & RETAIN':             '/warehouse/samples?tab=retain-boxes',
  'MATERIALS':                    '/warehouse/materials?tab=control-cards',
  'PHYSICAL & ALERTS':            '/warehouse/physical?tab=physical-inventory',
  'WAREHOUSE ADMIN':              '/warehouse/admin?tab=manager',

  'TRAINING PROGRAMS':            '/quality/training-programs',
  'TRAINING EVENTS':              '/quality/training-events',
  'TRAINING RECORDS':             '/training-records',
  'ONBOARDING':                   '/training/onboarding',
  'COMPETENCY':                   '/training/competency',
  'LMS':                          '/training/lms',

  'RELATIONSHIP MANAGER':         '/profiles/companies',
  'PIPELINE BOARD':               '/cm/pipeline-board',
  'SALES ORDERS':                 '/sales-orders',
  'SHIPMENTS':                    '/shipments',
  'BILL OF LADING':               '/logistics?tab=bol',
  'SHIPPING LOG':                 '/logistics?tab=log',

  'FINANCIAL OVERVIEW':           '/finance/fpa',
  'AP INVOICES':                  '/ap-invoices',
  'EXPENSES':                     '/expenses',

  'PEST MANAGEMENT':              '/safety/pest?tab=inspections',
  'GLASS & BRITTLE MATERIAL':     '/safety/glass?tab=log',
  'EMERGENCY PREPAREDNESS':       '/safety/emergency?tab=drills',
  'CHEMICAL & WASTE':             '/safety/chemical?tab=inventory',
  'FACILITY ACCESS':              '/safety/access?tab=visitor-log',
  'INSPECTIONS':                  '/safety/inspections?tab=gmp',
  'FACILITY CLEANING':            '/safety/facility-cleaning-hub',
  'PPE ISSUANCE':                 '/safety/ppe',
  'FDA REGISTRATIONS':            '/compliance/fda-registrations',

  'SOP LIBRARY':                  '/admin/sop-library',
  'DISPOSAL LOG':                 '/admin/retention?tab=disposal',
  'LEGAL':                        '/legal',
  'DATA VAULT':                   '/admin/data-vault-files',

  'REVENUE BY CUSTOMER':          '/reports/revenue-by-customer',
  'BATCH YIELD':                  '/reports/batch-yield',
  'NC FREQUENCY':                 '/reports/nc-frequency',
  'VENDOR ON-TIME DELIVERY':      '/reports/vendor-on-time',
  'SALES INTELLIGENCE':           '/reports/sales-intelligence',
  // REPORTS-CONSOLIDATION-1 (2026-09-19): Revenue Dashboard folded into the Sales
  // Performance hub as a tab.
  'REVENUE DASHBOARD':            '/reports/sales-performance?tab=revenue',
  'SALES PERFORMANCE':            '/reports/sales-performance',

  'PRODUCT CATALOG':              '/ing/catalog',
  'COST MATRIX':                  '/ing/cost-matrix',
  'QUOTES & PRICING':             '/ing/quotes',
  'TARIFF & FREIGHT':             '/ing/tariff?tab=tariff-schedule',
};

/** Find the most specific known route mentioned in a citation string: a
 *  PAGE_ROUTES page name first (longest first, so "Batch Costing Approvals"
 *  matches before a shorter collision could), then fall back to the coarser
 *  SECTION_ROUTES section-level match. */
function findCitationRoute(citationText) {
  if (!citationText) return null;
  const upper = citationText.toUpperCase();

  const pageKeys = Object.keys(PAGE_ROUTES).sort((a, b) => b.length - a.length);
  for (const key of pageKeys) {
    if (upper.includes(key)) return { section: key, route: PAGE_ROUTES[key] };
  }

  const sectionKeys = Object.keys(SECTION_ROUTES).sort((a, b) => b.length - a.length);
  for (const key of sectionKeys) {
    if (upper.includes(key)) return { section: key, route: SECTION_ROUTES[key] };
  }
  return null;
}

export default function HelpDrawer({ isOpen, onClose, initialModule }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useWindowWidth();
  const [question, setQuestion]         = useState('');
  const [answer, setAnswer]             = useState('');
  const [streaming, setStreaming]       = useState(false);
  const [feedbackId, setFeedbackId]     = useState(null);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [error, setError]               = useState(null);
  const abortRef = useRef(null);

  // Re-seed the module chip whenever the page changes while the drawer is open.
  const [currentModule, setCurrentModule] = useState(initialModule);
  useEffect(() => { setCurrentModule(initialModule); }, [initialModule]);

  useEffect(() => {
    if (!isOpen) {
      abortRef.current?.abort();
    }
  }, [isOpen]);

  // Stable refs so the route-change and Escape listeners below always call
  // the latest onClose/isOpen without re-subscribing on every parent render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  // Close on navigation while open -- never fires on the drawer's own mount.
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      if (isOpenRef.current) onCloseRef.current();
    }
  }, [location.pathname]);

  // Escape closes the drawer. Modal.jsx already handles this for the mobile
  // full-screen sheet -- this covers the desktop slide-in panel, which
  // doesn't route through Modal.jsx.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onCloseRef.current(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen]);

  async function handleSubmit(e) {
    e?.preventDefault();
    const q = question.trim();
    if (!q || streaming) return;

    setAnswer('');
    setError(null);
    setFeedbackId(null);
    setFeedbackSent(false);
    setStreaming(true);
    setQuestion('');

    abortRef.current = new AbortController();
    try {
      await streamHelpbotAsk(
        q,
        currentModule,
        (fbId) => setFeedbackId(fbId),
        (chunk) => setAnswer(prev => prev + chunk),
        abortRef.current.signal,
      );
    } catch (err) {
      if (err.name !== 'AbortError') setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setStreaming(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  async function handleFeedback(helpful) {
    if (!feedbackId || feedbackSent) return;
    setFeedbackSent(true);
    try { await sendHelpbotFeedback(feedbackId, helpful); } catch { /* non-critical */ }
  }

  const citationMatch = answer.match(/→ See:.*/);
  const citation = citationMatch ? citationMatch[0] : null;
  const answerBody = citation ? answer.replace(citation, '').trim() : answer;
  const citationLink = findCitationRoute(citation);

  function handleCitationClick() {
    if (!citationLink) return;
    navigate(citationLink.route);
    onClose();
  }

  const content = (
    <>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.875rem 1rem', borderBottom: '1px solid var(--color-border-neutral)' }}>
        <span className="ns-section-title">Help</span>
        <button
          onClick={onClose}
          aria-label="Close help"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: '1rem', lineHeight: 1, padding: '0.25rem' }}
        >✕</button>
      </div>

      {/* Module context chip */}
      {currentModule && MODULE_LABELS[currentModule] && (
        <div style={{ padding: '0.5rem 1rem 0' }}>
          <span style={{ display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.7rem', fontWeight: 600, background: 'var(--color-bg-primary)', color: 'var(--color-text-muted)' }}>
            {MODULE_LABELS[currentModule]}
          </span>
        </div>
      )}

      {/* Answer area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
        {!answer && !streaming && !error && (
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
            Ask a question about using the Littoral platform — I&apos;ll answer from the user guide.
          </p>
        )}
        {error && (
          <p style={{ color: 'var(--color-danger)', fontSize: '0.875rem' }}>{error}</p>
        )}
        {answerBody && (
          <p style={{ whiteSpace: 'pre-wrap', color: 'var(--color-text-primary)', fontSize: '0.875rem', lineHeight: 1.5, marginBottom: citation ? '0.75rem' : 0 }}>
            {answerBody}
            {streaming && <span aria-hidden="true">▌</span>}
          </p>
        )}
        {citation && citationLink && (
          <button
            onClick={handleCitationClick}
            title={`Go to ${citationLink.section}`}
            style={{ display: 'inline-block', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', border: '1px solid var(--color-accent)', background: 'none', color: 'var(--color-accent)', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}
          >
            {citation} →
          </button>
        )}
        {citation && !citationLink && (
          <div style={{ display: 'inline-block', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', border: '1px solid var(--color-accent)', color: 'var(--color-accent)', fontSize: '0.75rem', fontWeight: 600 }}>
            {citation}
          </div>
        )}
      </div>

      {/* Feedback row — visible once an answer has finished streaming */}
      {answer && !streaming && feedbackId && (
        <div style={{ padding: '0.5rem 1rem', borderTop: '1px solid var(--color-border-neutral)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
          {feedbackSent ? (
            <span>Thanks for the feedback.</span>
          ) : (
            <>
              <span>Was this helpful?</span>
              <button onClick={() => handleFeedback(true)} aria-label="Helpful" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', padding: '0.15rem 0.35rem' }}>👍</button>
              <button onClick={() => handleFeedback(false)} aria-label="Not helpful" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', padding: '0.15rem 0.35rem' }}>👎</button>
            </>
          )}
        </div>
      )}

      {/* Input row */}
      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '0.5rem', padding: '0.75rem 1rem', borderTop: '1px solid var(--color-border-neutral)' }}>
        <textarea
          value={question}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a question..."
          rows={1}
          disabled={streaming}
          style={{
            flex:         1,
            resize:       'none',
            padding:      '0.5rem 0.625rem',
            borderRadius: '0.375rem',
            border:       '1px solid var(--color-border-neutral)',
            background:   'var(--color-bg-primary)',
            color:        'var(--color-text-primary)',
            fontSize:     '0.875rem',
            fontFamily:   'inherit',
          }}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        data-lpignore="true"
        data-1p-ignore=""
        data-form-type="other"
        data-bwignore=""
        />
        <button
          type="submit"
          disabled={streaming || !question.trim()}
          style={{
            padding:      '0 0.875rem',
            borderRadius: '0.375rem',
            border:       'none',
            background:   'var(--color-accent)',
            color:        'var(--color-accent-btn-text)',
            fontWeight:   600,
            fontSize:     '0.8125rem',
            cursor:       streaming || !question.trim() ? 'default' : 'pointer',
            opacity:      streaming || !question.trim() ? 0.6 : 1,
          }}
        >
          Ask
        </button>
      </form>
    </>
  );

  if (isMobile) {
    if (!isOpen) return null;
    return (
      <Modal onClose={onClose} hideHeader>
        <div style={{ display: 'flex', flexDirection: 'column', height: '75vh' }}>
          {content}
        </div>
      </Modal>
    );
  }

  return (
    <>
      {isOpen && (
        <div
          onClick={onClose}
          aria-hidden="true"
          style={{ position: 'fixed', inset: 0, background: 'rgba(6,39,34,0.45)', zIndex: 899 }}
        />
      )}
      <div
        style={{
          position:      'fixed',
          right:         0,
          top:           0,
          height:        '100vh',
          width:         420,
          background:    'var(--color-bg-secondary)',
          borderLeft:    '1px solid var(--color-border-neutral)',
          boxShadow:     '-4px 0 16px rgba(0,0,0,0.15)',
          zIndex:        900,
          display:       'flex',
          flexDirection: 'column',
          transform:     isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition:    'transform 200ms ease',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Help"
      >
        {content}
      </div>
    </>
  );
}
